// import Debug from 'debug';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { Entity, Filter, getRowKey, Primitive, QueryOptions, Row } from 'gossamer-unified';
import { ErrorBoundary } from '../ancillary/ErrorBoundary';
import styles from './TreeNode.module.css';
import { useCacheQuery } from '../data/useCacheQuery';

// const debug = Debug('gfe/se/Tree');

interface TreeNodeRenderProps<T extends Row> {
  index?: number;
  isLast?: boolean;
  row: T;
}

interface TreeNodeProps<T extends Row> {
  childQueryOptions: QueryOptions;
  displayText?: (row: T) => string;
  entity: Entity<T>;
  index?: number;
  initiallyExpanded?: (row: T) => boolean;
  isExpandable?: (row: T) => boolean;
  isLast?: boolean;
  onExpand?: (row: T) => void;
  onContract?: (row: T) => void;
  render?: (props: TreeNodeRenderProps<T>) => React.JSX.Element;
  row: T;
}

export const TreeNode = <T extends Row>(props: TreeNodeProps<T>): React.JSX.Element => {
  const [open, setOpen] = React.useState(props.initiallyExpanded ? props.initiallyExpanded(props.row) : false);
  const queryOptions = cloneDeep(props.childQueryOptions);
  queryOptions?.where?.and?.forEach((filter) => {
    if ((filter as Filter).value === '$PARENT') {
      (filter as Filter).value = getRowKey(props.entity, props.row) as Primitive;
    }
  });
  const queryChildren = useCacheQuery(props.entity, queryOptions, !open);
  const isExpandable = props.isExpandable ? props.isExpandable(props.row) : false;
  const twistieIconSrc = open ? '/assets/icons/arrow-down.svg' : '/assets/icons/arrow-right.svg';
  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isExpandable) {
      setOpen(!open);
      if (!open && props.onExpand) {
        props.onExpand(props.row);
      } else if (open && props.onContract) {
        props.onContract(props.row);
      }
    }
  };
  const Render = props.render
    ? props.render
    : () => <span>{props.displayText ? props.displayText(props.row) : 'Please supply render or displayText'}</span>;
  return (
    <li className={styles.TreeNode}>
      <div>
        {isExpandable && (
          <a title={open ? 'Hide the contents' : 'Show the contents'}>
            <img className={styles.Twistie} onClick={handleIconClick} src={twistieIconSrc} />
          </a>
        )}
        <Render row={props.row} index={props.index} isLast={props.isLast} />
      </div>
      {open && !!queryChildren.error && <div>Error loading children</div>}
      {open && queryChildren.isFetching && <div>Loading...</div>}
      {open && !!queryChildren.data && (
        <ul>
          {queryChildren.data.map((childRow, index) => (
            <ErrorBoundary key={getRowKey(props.entity, childRow)}>
              <TreeNode
                childQueryOptions={props.childQueryOptions}
                displayText={props.displayText}
                entity={props.entity}
                initiallyExpanded={props.initiallyExpanded}
                isExpandable={props.isExpandable}
                render={props.render}
                row={childRow}
                index={index}
                isLast={index === (queryChildren.data?.length ?? 0) - 1}
                onExpand={props.onExpand}
                onContract={props.onContract}
              />
            </ErrorBoundary>
          ))}
        </ul>
      )}
    </li>
  );
};
